import React, { ReactNode, useContext, useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
import Skeleton from 'react-loading-skeleton';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { OrderScreen } from '../OrderScreen';
import { OrderSummaryGrid } from '../components/summary/OrderSummaryGrid';
import { OrderSummaryOptions, showHeight } from '../components/summary/OrderSummaryOptions';
import { ActivityIndicator } from '../../../components/activity-indicator/ActivityIndicator';
import { useCartPlaceOrder } from '../utils/useCartPlaceOrder';
import { OrderSuccessGridData } from '../components/success/OrderSuccessGrid';

import { Routes } from 'routes';
import { EASE_IN_OUT_BACK } from 'styles/easing';
import { Btn } from 'components/btn/Btn';
import { useRootSelector } from 'common/features/featuresReducer';
import { createStyles } from 'utils/createStyle';
import { resetCart } from 'common/features/store/duck/cart/duck';
import { LoadingOverlay, closeLoader, openLoader } from 'components/loading-overlay/LoadingOverlay';
import {
    OrderScreenContext,
    CheckoutFlowState,
} from 'features/order-summary/screens/CheckoutScreen';
import { PlaceOrderResult } from 'common/api/e-comm/orders/service';
import { Modal, ModalSize } from 'components/modal/Modal';
import { putDefaultAddress } from 'common/api/crm/family/address';
import { isInIframe, mainWebsiteOrigin } from 'utils';
import { Branding, IS_FLIPGIVE } from 'common/env';
import { logOrderReviewViewed } from 'config/analytics';

const styles = createStyles({
    area: [
        'mb-2',
        {
            overflow: 'hidden',
            animationName: showHeight,
            animationDuration: '.5s',
            animationTimingFunction: EASE_IN_OUT_BACK,
            animationFillMode: 'forwards',
        },
    ],
});

const renderProcessingButton = (title: ReactNode) => {
    return (
        <div className="flex items-center justify-center py-2 font-bold text-lg text-brand-dark">
            <span className="-ml-4 inline-block mr-2">
                <ActivityIndicator width="20px" />
            </span>
            <span>{title}...</span>
        </div>
    );
};

export const OrderReviewScreenContent = () => {
    const { cart, cartAsyncState } = useRootSelector((s) => s.store.cart);
    const { cartInfo, setCartInfo, cartInfoDetails, checkoutFlowState } =
        useContext(OrderScreenContext);
    const ref = useRef<LoadingOverlay>(null);
    const disableCheckoutBtn = checkoutFlowState === CheckoutFlowState.DISABLED;

    const defaultOrg = useRootSelector((s) => {
        const homeDetails = s.store.home.homeDetails;

        if (homeDetails?.organizations && homeDetails?.defaultOrganization) {
            return homeDetails?.organizations[homeDetails?.defaultOrganization?.id];
        }
    });

    const canCheckout = Boolean(defaultOrg?.isActive && defaultOrg?.organization?.isActive);

    const { placeOrder } = useCartPlaceOrder({
        onThreeDSecureChallengeEnd: () => {
            closeLoader(ref.current);
        },
        onThreeDSecureChallengeStart: () => {
            openLoader(ref.current);
        },
    });
    const dispatch = useDispatch<any>();
    const history = useHistory();
    const [showOnHoldBanner, setShowOnHoldBanner] = useState<boolean>(false);

    useEffect(() => {
        // Checkout renders inside the payment-host iframe, where PostHog is not
        // initialized (see PostHogGate's isPaymentHost guard). Forward to the parent
        // main-host frame to capture the event there, mirroring the `view_checkout`
        // pattern handled in OrderScreen. A direct posthog.capture() here is a no-op.
        if (isInIframe()) {
            window.parent.postMessage({ type: 'order_review_viewed' }, mainWebsiteOrigin());
        } else {
            logOrderReviewViewed();
        }
    }, []);

    // Does not allow the user to view the review page if they have not set their payment info
    useEffect(() => {
        if (!cartInfo.paymentMethodId) {
            history.replace(Routes.ShopOrderSummary);
        }
    }, [cartInfo.paymentMethodId, history]);

    const {
        shipping: { selectedShipping, isLoading: isLoadingShipping, shippingFee },
        payment: { paymentFee },
        total,
    } = cartInfoDetails;

    const { shipToHome } = cartInfo;

    const setShipToHome = (value: boolean) => {
        setCartInfo({
            ...cartInfo,
            shipToHome: value,
        });
    };

    const [isPlacingOrder, setIsPlacingOrder] = useState(false);

    const doPlaceOrder = async () => {
        try {
            setIsPlacingOrder(true);

            const response = await placeOrder({});
            let data: OrderSuccessGridData | undefined = undefined;
            if (response) {
                if (response.data && response.data.orderId) {
                    if (cart) {
                        data = {
                            data: response.data,
                            cart,
                            shipToHome: Boolean(shipToHome),
                            paymentFee,
                            selectedShipping,
                            total,
                            shippingFee,
                        };

                        // updates the default shipping address
                        if (cartInfo.setShippingAddressAsDefault && cartInfo.shippingToAddressId) {
                            await putDefaultAddress(cartInfo.shippingToAddressId);
                        }
                    }

                    if (response.data?.result === PlaceOrderResult.Hold) {
                        setShowOnHoldBanner(true);
                        return;
                    }

                    if (data) {
                        window.parent.postMessage(
                            {
                                type: 'orderPlaced',
                                orderSuccessGridData: data,
                                linkingWithClo: Boolean(
                                    !IS_FLIPGIVE &&
                                        (cartInfo.linkNewCardWithClo ||
                                            cartInfo.cardToLinkWithClo ===
                                                cartInfo.paymentMethodId?.id)
                                ),
                            },
                            mainWebsiteOrigin()
                        );
                    }
                    dispatch(resetCart());
                }
            }
        } finally {
            setIsPlacingOrder(false);
        }
    };

    const renderPlaceOrderButton = () => {
        if (!isPlacingOrder) {
            return (
                <Btn
                    className="cart_place_order_button w-full mt-4"
                    onClick={canCheckout ? doPlaceOrder : undefined}
                    disabled={isLoadingShipping || disableCheckoutBtn || !canCheckout}
                >
                    Place Order
                </Btn>
            );
        }

        return renderProcessingButton('Placing Order');
    };

    const cancelOrder = () => history.push(Routes.ShopOrderSummary, cartInfo);

    const onCloseOnHold = () => {
        dispatch(resetCart());

        if (isInIframe()) {
            window.parent.postMessage({ type: 'closeOnHold' }, mainWebsiteOrigin());
        } else {
            history.push(Routes.Shop);
        }
    };

    const renderOrderOptions = () => {
        return (
            <div className="hidden md:block" style={{ width: '20%', minWidth: '18rem' }}>
                {cart && !cartAsyncState.loading && (
                    <OrderSummaryOptions shipToHome={Boolean(shipToHome)} hidePayment={false}>
                        <div className={classNames(styles.area, 'hidden md:block mt-5')}>
                            {renderPlaceOrderButton()}
                            <Btn
                                isSecondary={true}
                                className="cart_cancel_order_button w-full mt-4"
                                onClick={cancelOrder}
                            >
                                Cancel
                            </Btn>
                        </div>
                    </OrderSummaryOptions>
                )}
                {cartAsyncState.loading && (
                    <>
                        <Skeleton height="10rem" />
                    </>
                )}
            </div>
        );
    };

    return (
        <>
            {showOnHoldBanner ? (
                <Modal
                    title="Order Pending"
                    isOpen={showOnHoldBanner}
                    showCloseButton={false}
                    onClose={onCloseOnHold}
                    titleStyles="text-error font-semibold sm:text-lg text-lg"
                    size={[ModalSize.CONTENT_WIDTH, ModalSize.MEDIUM]}
                >
                    <p className="text-grey-1 text-sm">
                        This order is currently being reviewed and placed in a pending status.
                        You’ll receive an order confirmation by email once it processes. Any
                        subsequent orders will also be held until processed.
                    </p>
                    <button
                        onClick={onCloseOnHold}
                        className="ml-auto mr-auto w-2/3 rounded-full bg-brand text-white font-semibold text-lg py-2 mt-10"
                    >
                        Back to {Branding}.com
                    </button>
                </Modal>
            ) : null}
            <div className="md:flex flex-row gap-4">
                {cart && (
                    <>
                        <div className="w-full">
                            <OrderSummaryGrid
                                cart={cart}
                                loading={false}
                                shipToHome={Boolean(shipToHome)}
                                onShipToHomeChange={setShipToHome}
                                hideButtons={true}
                                summaryType="total"
                                hideEditQuantityButtons
                                hideDeleteButton
                            />
                        </div>
                        {renderOrderOptions()}
                        <div
                            className={classNames(
                                styles.area,
                                'block md:hidden mt-5 w-full text-center'
                            )}
                        >
                            <div className="inline-block max-w-xs">
                                {renderPlaceOrderButton()}
                                <Btn
                                    isSecondary={true}
                                    className="cart_cancel_order_button w-full mt-4"
                                    onClick={cancelOrder}
                                >
                                    Cancel
                                </Btn>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export const OrderReviewScreen = () => {
    return (
        <OrderScreen
            title="Order Review"
            orderStep="review"
            showSummary={false}
            showMobileSummary={false}
            embedView={true}
        >
            <OrderReviewScreenContent />
        </OrderScreen>
    );
};
